import {
  Alert,
  Box,
  Button,
  Container,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { Link as RouterLink, useNavigate } from 'react-router'
import { useMutation, useQuery } from 'urql'
import PageHeader from '../../components/PageHeader'
import { graphql } from '../../gql'
import { CreateProjectInput } from '../../gql/graphql'
import { useDocumentTitle } from '../../hooks/useDocumentTitle'

interface IFormInput {
  name: string
  clientId: number
}

const createProjectMutation = graphql(`
  mutation AddProjectPageCreateProject($input: CreateProjectInput!) {
    CreateProject(input: $input) {
      id
      name
    }
  }
`)

export default function AddProjectPage() {
  useDocumentTitle('Add a Project')
  const navigate = useNavigate()
  const [formQueryResult] = useQuery({
    query: graphql(`
      query AddProjectPage {
        Clients {
          id
          name
        }
      }
    `),
    requestPolicy: 'network-only',
  })

  const [createProjectResult, createProject] = useMutation(createProjectMutation)

  const { handleSubmit, control, formState } = useForm<IFormInput>({
    defaultValues: {
      name: '',
      clientId: -1,
    },
  })

  const onSubmit: SubmitHandler<IFormInput> = async (data) => {
    if (createProjectResult.fetching) return
    const input: CreateProjectInput = { ...data }
    await createProject({ input }).then((r) => {
      if (!r.error) {
        if (!r.data) {
          console.error('No data returned from createProject mutation')
          return
        }
        navigate(`/projects/${r.data.CreateProject.id}`)
      }
    })
  }

  if (formQueryResult.data?.Clients && formQueryResult.data.Clients.length === 0) {
    return (
      <Stack spacing={2} flex="1">
        <Typography>
          You don't have any clients yet. Please{' '}
          <Link component={RouterLink} to="/clients">
            add a client
          </Link>{' '}
          first.
        </Typography>
      </Stack>
    )
  }

  return (
    <Stack spacing={2} flex="1">
      <PageHeader title="Add a Project" />
      <Container maxWidth="xs" component="form" onSubmit={handleSubmit(onSubmit)}>
        <Stack sx={{ ml: 0, mt: 2 }} spacing={4} alignItems="flex-start">
          <Controller
            name="name"
            control={control}
            rules={{
              required: true,
              maxLength: 255,
              minLength: { value: 3, message: 'Must be longer than 3 characters' },
            }}
            render={({ field, fieldState }) => (
              <TextField
                autoFocus
                required
                margin="dense"
                label="Name"
                fullWidth
                autoComplete="off"
                variant="standard"
                {...field}
                disabled={createProjectResult.fetching}
                error={!!fieldState.error}
                helperText={fieldState.error && (fieldState.error?.message || 'Required')}
              />
            )}
          />
          <Controller
            name="clientId"
            control={control}
            rules={{
              required: true,
              validate: (value) => value !== -1 || 'Please select a client',
            }}
            render={({ field, fieldState }) => {
              if (!formQueryResult.data || !formQueryResult.data.Clients) {
                return <Box>No clients</Box>
              }
              return (
                <FormControl fullWidth required>
                  <InputLabel id="client-select-label">Client</InputLabel>
                  <Select {...field} error={!!fieldState.error} labelId="client-select-label" label="Client" required>
                    <MenuItem value={-1} disabled>
                      Select a client
                    </MenuItem>
                    {formQueryResult.data.Clients.map((client) => (
                      <MenuItem key={client.id} value={client.id}>
                        {client.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )
            }}
          />

          {createProjectResult.error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <p>An error has occurred:</p>
              {createProjectResult.error.message}
            </Alert>
          )}
          <Button type="submit" variant="contained" disabled={createProjectResult.fetching || !formState.isValid}>
            Submit
          </Button>
        </Stack>
      </Container>
    </Stack>
  )
}
